/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectorRef, ViewRef} from '@angular/core';
import {finalize, Observable, take, timeout} from 'rxjs';
import {CommonHelperService} from '../service/common-helper.service';

export const ICD_COMPARE_EMPTY_CONTENT_MESSAGE = 'ICD compare returned empty content.';
export const ICD_COMPARE_FAILED_MESSAGE = 'ICD compare failed.';

const ICD_COMPARE_TIMEOUT_MS = 120000;
const ICD_COMPARE_BLOB_TYPE = 'application/octet-stream;charset=utf-8';

export function icdCompareFailureMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
        if (err.status >= 400 && err.status < 500) {
            const message = messageFromHttpErrorBody(err.error);
            if (message) {
                return message;
            }
        }
        if (err.status >= 500) {
            return ICD_COMPARE_FAILED_MESSAGE;
        }
        return err.message || ICD_COMPARE_FAILED_MESSAGE;
    }
    if (err instanceof Error && err.message) {
        return err.message;
    }
    return ICD_COMPARE_FAILED_MESSAGE;
}

function messageFromHttpErrorBody(body: unknown): string {
    const parsed = parseHttpErrorBody(body);
    if (parsed && typeof parsed.message === 'string' && parsed.message) {
        return parsed.message;
    }
    return '';
}

function parseHttpErrorBody(body: unknown): {message?: unknown} | null {
    if (isBinaryHttpErrorBody(body)) {
        try {
            return JSON.parse(new TextDecoder().decode(body));
        } catch {
            return null;
        }
    }
    if (body && typeof body === 'object') {
        return body as {message?: unknown};
    }
    return null;
}

function isBinaryHttpErrorBody(body: unknown): body is ArrayBuffer | ArrayBufferView {
    return body instanceof ArrayBuffer
        || ArrayBuffer.isView(body)
        || Object.prototype.toString.call(body) === '[object ArrayBuffer]';
}

export function handleIcdCompareKeydown(event: KeyboardEvent, exportFn: () => void): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
        return;
    }
    event.preventDefault();
    exportFn();
}

export function setExportingWithDetectChanges(
    host: {exporting: boolean},
    changeDetectorRef: ChangeDetectorRef,
    value: boolean
): void {
    host.exporting = value;
    const viewRef = changeDetectorRef as ViewRef;
    if (viewRef.destroyed) {
        return;
    }
    changeDetectorRef.detectChanges();
}

export function subscribeIcdCompareDownload(
    request$: Observable<ArrayBuffer>,
    options: {
        setExporting: (value: boolean) => void;
        fileName: string;
        onEmpty: (message: string) => void;
        onError: (message: string) => void;
    }
): void {
    options.setExporting(true);
    request$.pipe(
        take(1),
        timeout({first: ICD_COMPARE_TIMEOUT_MS}),
        finalize(() => options.setExporting(false))
    ).subscribe({
        next: (data: ArrayBuffer) => {
            // Opening the file happens outside Angular; clear exporting on first emission so the
            // indeterminate bar does not stay up after the payload is already saved.
            options.setExporting(false);
            if (!data || data.byteLength < 1) {
                options.onEmpty(ICD_COMPARE_EMPTY_CONTENT_MESSAGE);
                return;
            }
            CommonHelperService.downloadFile(new Blob([data], {type: ICD_COMPARE_BLOB_TYPE}), options.fileName);
        },
        error: (err: unknown) => {
            options.onError(icdCompareFailureMessage(err));
        }
    });
}
